import Decimal from "decimal.js";
import { TokenList } from "../store/global/getAllTokenLists";
import formatLocaleNumber, { fallbackDisplayDecimals } from "../utils/amountUtils";
import { Hash } from "viem";
import { takeWhile } from "lodash-es";
import { Text } from "../components/general/Text";
import { SUBSCRIPT_AMOUNT_DECIMALS } from "../config/consts";

Decimal.set({ precision: 28 }); // Set global Decimal Precision
export class Amount {
  public static ZERO: Amount = new Amount("0");
  public value: Decimal;
  public formatted: string;

  constructor(value: string) {
    this.value = new Decimal(value);
    this.formatted = this.value.toString();
  }

  mul(otherAmount: Amount): Amount {
    const newVal = this.value.mul(otherAmount.value);
    return new Amount(newVal.toString());
  }

  add(otherAmount: Amount): Amount {
    const newVal = this.value.add(otherAmount.value);
    return new Amount(newVal.toString());
  }

  div(otherAmount: Amount): Amount {
    const newVal = this.value.div(otherAmount.value);
    return new Amount(newVal.toString());
  }

  sub(otherAmount: Amount): Amount {
    return new Amount(this.value.sub(otherAmount.value).toString());
  }

  toString(): string {
    return this.value.toString();
  }

  toDisplay(tokenAddress: Hash, tokenList: TokenList | undefined): string;
  // eslint-disable-next-line @typescript-eslint/unified-signatures
  toDisplay(tokenAddress: Hash, decimals: number): string;
  toDisplay(
    tokenAddress: Hash,
    displayDecimalsOrTokenList: number | TokenList | undefined
  ): string {
    const locale = navigator.language;
    const isArgumentANumber = typeof displayDecimalsOrTokenList === "number";
    const displayDecimals = isArgumentANumber
      ? displayDecimalsOrTokenList
      : fallbackDisplayDecimals(tokenAddress, displayDecimalsOrTokenList);
    // We ROUND_HALF_CEIL as this is what's classed as classic rounding
    const roundedValue = this.value.toDP(displayDecimals, Decimal.ROUND_HALF_CEIL);
    const stringValue = roundedValue.toFixed(displayDecimals);
    const valueToDisplay = formatLocaleNumber({
      number: stringValue,
      locale: locale,
      displayDecimals,
    });
    return valueToDisplay;
  }

  toShortFormat(): string {
    const num = this.value.toNumber();
    if (num < 1e3) {
      return `$${num.toFixed(2)}`;
    } else if (num < 1e4) {
      return `$${(num / 1e3).toFixed(2)}K`;
    } else if (num < 1e5) {
      return `$${(num / 1e3).toFixed(1)}K`;
    } else if (num < 1e6) {
      return `$${(num / 1e3).toFixed(0)}K`;
    } else if (num < 1e7) {
      return `$${(num / 1e6).toFixed(2)}M`;
    } else if (num < 1e8) {
      return `$${(num / 1e6).toFixed(1)}M`;
    } else if (num < 1e9) {
      return `$${(num / 1e6).toFixed(0)}M`;
    } else if (num < 1e10) {
      return `$${(num / 1e9).toFixed(2)}B`;
    } else if (num < 1e11) {
      return `$${(num / 1e9).toFixed(1)}B`;
    } else if (num < 1e12) {
      return `$${(num / 1e9).toFixed(0)}B`;
    } else {
      return `$${(num / 1e12).toFixed(1)}T`;
    }
  }

  toFormattedSubscript(showDollarSymbol: boolean): undefined | React.ReactNode {
    const [integerPart, fullDecimalPart = ""] = this.value.toFixed().split(".");

    const leadingZerosCount = takeWhile([...fullDecimalPart], digit => digit === "0").length;

    const significantPart = fullDecimalPart.substring(leadingZerosCount);
    const dollarSymbol = showDollarSymbol ? "$" : "";

    if (leadingZerosCount >= SUBSCRIPT_AMOUNT_DECIMALS && integerPart === "0") {
      const subscriptText = significantPart.substring(0, 2);
      return (
        <span>
          {dollarSymbol}
          {integerPart}.0
          <Text as={"sub"} textStyle={"Title"} size={"XS"}>
            {leadingZerosCount}
          </Text>
          {subscriptText}
        </span>
      );
    }
  }

  format(fractionalDigits: number): string {
    return Intl.NumberFormat(navigator.language, {
      maximumFractionDigits: fractionalDigits,
      minimumFractionDigits: fractionalDigits,
    }).format(this.value.toDP(fractionalDigits).toNumber());
  }
}
